import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Pricing() {
  const [calculatorTab, setCalculatorTab] = useState<'renter' | 'host'>('renter');
  
  // Renter Calculator State
  const [rentalPrice, setRentalPrice] = useState(500);
  const [rentalMonths, setRentalMonths] = useState(3);
  
  // Host Calculator State
  const [hostPrice, setHostPrice] = useState(400);
  const [hostMonths, setHostMonths] = useState(6);

  const serviceFeePercent = 10;
  const hostFeePercent = 10;

  // Renter Calculations
  const subtotal = rentalPrice * rentalMonths;
  const serviceFee = subtotal * (serviceFeePercent / 100);
  const renterTotal = subtotal + serviceFee;

  // Host Calculations
  const hostGross = hostPrice * hostMonths;
  const hostFee = hostGross * (hostFeePercent / 100);
  const hostNet = hostGross - hostFee;
  const annualEarnings = (hostNet / hostMonths) * 12; // Calculate annual based on monthly rate

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-indigo-600 to-indigo-800 text-white py-20 pt-36">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Simple, Transparent Pricing</h1>
          <p className="text-xl text-indigo-100 max-w-3xl mx-auto">
            No hidden fees. No surprises. Just straightforward pricing that works for everyone.
          </p>
        </div>
      </section>

      {/* Pricing Overview */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 mb-20">
            {/* For Renters */}
            <div className="bg-gradient-to-br from-indigo-50 to-white p-10 rounded-2xl border-2 border-indigo-200 shadow-lg">
              <div className="w-16 h-16 flex items-center justify-center bg-indigo-600 rounded-full mb-6">
                <i className="ri-user-line text-3xl text-white"></i>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">For Renters</h2>
              <div className="mb-6">
                <div className="text-5xl font-bold text-indigo-600 mb-2">10%</div>
                <div className="text-gray-600 text-lg">Service Fee</div>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-green-600 mt-1"></i>
                  <span className="text-gray-700">Added to the listing price at checkout</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-green-600 mt-1"></i>
                  <span className="text-gray-700">Covers payment processing and insurance</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-green-600 mt-1"></i>
                  <span className="text-gray-700">24/7 customer support included</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-green-600 mt-1"></i>
                  <span className="text-gray-700">Secure payment protection</span>
                </li>
              </ul>
            </div>

            {/* For Hosts */}
            <div className="bg-gradient-to-br from-green-50 to-white p-10 rounded-2xl border-2 border-green-200 shadow-lg">
              <div className="w-16 h-16 flex items-center justify-center bg-green-600 rounded-full mb-6">
                <i className="ri-home-smile-line text-3xl text-white"></i>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">For Hosts</h2>
              <div className="mb-6">
                <div className="text-5xl font-bold text-green-600 mb-2">10%</div>
                <div className="text-gray-600 text-lg">Host Fee</div>
              </div>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-green-600 mt-1"></i>
                  <span className="text-gray-700">Deducted from your earnings</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-green-600 mt-1"></i>
                  <span className="text-gray-700">Covers platform maintenance and support</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-green-600 mt-1"></i>
                  <span className="text-gray-700">Free listing creation and management</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-2xl text-green-600 mt-1"></i>
                  <span className="text-gray-700">Secure and timely payouts</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Interactive Calculators */}
          <div id="calculator" className="bg-gray-50 rounded-2xl p-8 md:p-12">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Calculate Your Costs & Earnings
              </h2>
              <p className="text-xl text-gray-600">
                See exactly what you'll pay or earn with our interactive calculators
              </p>
            </div>

            {/* Calculator Tab Switcher */}
            <div className="flex justify-center mb-12">
              <div className="inline-flex rounded-full bg-white p-1 shadow-md">
                <button
                  onClick={() => setCalculatorTab('renter')}
                  className={`px-8 py-3 rounded-full font-semibold transition-all whitespace-nowrap ${
                    calculatorTab === 'renter'
                      ? 'bg-indigo-600 text-white shadow-md'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Renter Calculator
                </button>
                <button
                  onClick={() => setCalculatorTab('host')}
                  className={`px-8 py-3 rounded-full font-semibold transition-all whitespace-nowrap ${
                    calculatorTab === 'host'
                      ? 'bg-green-600 text-white shadow-md'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Host Calculator
                </button>
              </div>
            </div>

            {/* Renter Calculator */}
            {calculatorTab === 'renter' && (
              <div className="max-w-4xl mx-auto">
                <div className="bg-white rounded-xl p-8 shadow-lg">
                  <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
                    How Much Will My Rental Cost?
                  </h3>
                  
                  <div className="grid md:grid-cols-2 gap-8 mb-8">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Monthly Rental Price
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                        <input
                          type="number"
                          value={rentalPrice}
                          onChange={(e) => setRentalPrice(Number(e.target.value))}
                          className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg text-lg focus:border-indigo-500 focus:outline-none"
                          min="1"
                        />
                      </div>
                      <input
                        type="range"
                        value={rentalPrice}
                        onChange={(e) => setRentalPrice(Number(e.target.value))}
                        min="100"
                        max="5000"
                        className="w-full mt-4"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Number of Months
                      </label>
                      <input
                        type="number"
                        value={rentalMonths}
                        onChange={(e) => setRentalMonths(Number(e.target.value))}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-lg focus:border-indigo-500 focus:outline-none"
                        min="1"
                      />
                      <input
                        type="range"
                        value={rentalMonths}
                        onChange={(e) => setRentalMonths(Number(e.target.value))}
                        min="1"
                        max="24"
                        className="w-full mt-4"
                      />
                    </div>
                  </div>

                  <div className="border-t-2 border-gray-200 pt-6">
                    <div className="space-y-4 mb-6">
                      <div className="flex justify-between text-lg">
                        <span className="text-gray-600">Subtotal ({rentalMonths} {rentalMonths === 1 ? 'month' : 'months'} × ${rentalPrice})</span>
                        <span className="font-semibold text-gray-900">${subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-lg">
                        <span className="text-gray-600">Service Fee (10%)</span>
                        <span className="font-semibold text-gray-900">${serviceFee.toFixed(2)}</span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center bg-indigo-50 p-6 rounded-lg">
                      <span className="text-2xl font-bold text-gray-900">Total Cost</span>
                      <span className="text-4xl font-bold text-indigo-600">${renterTotal.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Host Calculator */}
            {calculatorTab === 'host' && (
              <div className="max-w-4xl mx-auto">
                <div className="bg-white rounded-xl p-8 shadow-lg">
                  <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
                    How Much Can I Earn as a Host?
                  </h3>
                  
                  <div className="grid md:grid-cols-2 gap-8 mb-8">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Monthly Listing Price
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                        <input
                          type="number"
                          value={hostPrice}
                          onChange={(e) => setHostPrice(Number(e.target.value))}
                          className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg text-lg focus:border-green-500 focus:outline-none"
                          min="1"
                        />
                      </div>
                      <input
                        type="range"
                        value={hostPrice}
                        onChange={(e) => setHostPrice(Number(e.target.value))}
                        min="100"
                        max="5000"
                        className="w-full mt-4"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Number of Months
                      </label>
                      <input
                        type="number"
                        value={hostMonths}
                        onChange={(e) => setHostMonths(Number(e.target.value))}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-lg focus:border-green-500 focus:outline-none"
                        min="1"
                        max="24"
                      />
                      <input
                        type="range"
                        value={hostMonths}
                        onChange={(e) => setHostMonths(Number(e.target.value))}
                        min="1"
                        max="24"
                        className="w-full mt-4"
                      />
                    </div>
                  </div>

                  <div className="border-t-2 border-gray-200 pt-6">
                    <div className="space-y-4 mb-6">
                      <div className="flex justify-between text-lg">
                        <span className="text-gray-600">Gross Earnings ({hostMonths} {hostMonths === 1 ? 'month' : 'months'} × ${hostPrice})</span>
                        <span className="font-semibold text-gray-900">${hostGross.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-lg">
                        <span className="text-gray-600">Host Fee (10%)</span>
                        <span className="font-semibold text-red-600">-${hostFee.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-lg">
                        <span className="text-gray-600">Net Earnings ({hostMonths} {hostMonths === 1 ? 'month' : 'months'})</span>
                        <span className="font-semibold text-gray-900">${hostNet.toFixed(2)}</span>
                      </div>
                    </div>
                    <div className="bg-green-50 p-6 rounded-lg space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-2xl font-bold text-gray-900">You Earn</span>
                        <span className="text-4xl font-bold text-green-600">${hostNet.toFixed(2)}</span>
                      </div>
                      <div className="text-center pt-4 border-t border-green-200">
                        <p className="text-sm text-gray-600 mb-1">Potential Annual Earnings</p>
                        <p className="text-3xl font-bold text-green-700">${annualEarnings.toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Additional Benefits */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What's Included in Our Fees
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Your fees cover essential services that make STASHLY safe and reliable
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-md text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                <i className="ri-lock-line text-2xl text-green-600"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Secure Payments</h3>
              <p className="text-sm text-gray-600">Bank-level encryption</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-purple-100 rounded-full mx-auto mb-4">
                <i className="ri-customer-service-2-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">24/7 Support</h3>
              <p className="text-sm text-gray-600">Always here to help</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-orange-100 rounded-full mx-auto mb-4">
                <i className="ri-verified-badge-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">User Verification</h3>
              <p className="text-sm text-gray-600">Trusted community</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Pricing FAQs
          </h2>
          
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-3">When do I pay the service fee?</h3>
              <p className="text-gray-600">The service fee is added to your total at checkout. You'll see the full breakdown before confirming your booking.</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-3">When do hosts get paid?</h3>
              <p className="text-gray-600">Hosts receive payment 24 hours after the booking start date. Payments are transferred directly to your nominated bank account.</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Are there any hidden fees?</h3>
              <p className="text-gray-600">No. The fees shown are the only fees charged. What you see is what you pay.</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Can I offer discounts to renters?</h3>
              <p className="text-gray-600">Yes! Hosts can set weekly and monthly discounts. The host fee remains 10% regardless of discounts offered.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-indigo-600 to-indigo-800 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Start Earning or Renting?
          </h2>
          <p className="text-xl text-indigo-100 mb-10">
            Join STASHLY today and unlock the value of unused spaces
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/signup"
              className="px-8 py-4 bg-yellow-400 hover:bg-yellow-300 text-yellow-900 font-bold text-lg rounded-xl transition-colors whitespace-nowrap cursor-pointer text-center"
            >
              Create Free Account
            </a>
            <a
              href="/browse"
              className="px-8 py-4 bg-white text-indigo-600 font-semibold rounded-lg hover:bg-indigo-50 transition-colors whitespace-nowrap cursor-pointer text-center"
            >
              Browse Spaces
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
